/**
 * AdvanceCollection Component
 *
 * @description
 * Manages advance payment flow:
 *  - Select payer type (guest/company)
 *  - Search & select guest
 *  - Show guest info + previous advances
 *
 * @state
 *  - payerType: current payer selection
 *  - selectedGuest: chosen guest object
 *  - allBranches: search scope toggle
 */
import React, { useState, useEffect, useContext } from 'react';
import { Row } from 'antd';
import { Card, Button, Location, useTranslation, ReportingDashboard, GlobalContext } from 'soxo-bootstrap-core';
import SectionHeaderonHeader from './components/section-header';
import PayerSelector from './components/payer-selector';
import PayerSearch from './components/payer-search';
import GuestInfo from './components/info-card';
import './advance-collection.scss';
import OpregSearch from '../candidate-visit/opreg-search';
import { Appointments, Opreg } from '../../models';

const dpbtr = localStorage.getItem('db_ptr');

// Define available payer types
export const payerAvailableTypes = {
    GUEST: 'guest',
    CORPORATE: 'corporate',
};
// Define available payer modes
export const payerModes = {
    guest: 'P',
    corporate: 'C',
};

const AdvanceCollection = (props) => {
    const { t, i18n } = useTranslation();

    //variable to get the settings
    const { settings } = useContext(GlobalContext);

    //variable to show the guest button or corporate button
    const { enableGuestAdvance } = settings?.ADVSET || {};

    // Default payer type
    const defaultPayer = enableGuestAdvance ? payerAvailableTypes.GUEST : payerAvailableTypes.CORPORATE;

    // State for current payer type (default: guest)
    const [payerType, setPayerType] = useState(defaultPayer);

    // Stores selected guest info (null until chosen)
    const [selectedGuest, setSelectedGuest] = useState(null);

    // const params = new URLSearchParams(window.location.search);
    let urlParams = Location.search();

    const id = urlParams.id;
    const type = urlParams.type;

    const effectiveType = type || payerType;

    /** ---------------------------
     * Restore guest on refresh
     * -------------------------- */

    const fetchPayerFromUrl = async () => {
        if (id && type) {
            try {
                const formBody = {
                    search_by: type === payerAvailableTypes.GUEST ? 'op_number' : 'code',
                    criteria: 'end_with',
                    search_text: id,
                    index: dpbtr,
                    mode: type,
                };

                const response = await Appointments.consolidatedSearch(formBody);

                if (response?.success && response.result?.length > 0) {
                    let guestData = response.result[0];

                    // Normalize corporate code if missing
                    if (effectiveType === payerAvailableTypes.CORPORATE && !guestData.code) {
                        guestData.code = guestData.corporate_code || guestData.op_number;
                    }

                    setSelectedGuest(guestData);
                    setPayerType(effectiveType);
                }
            } catch (err) {
                console.error('Failed to restore guest:', err);
            }
        }
    };

    useEffect(() => {
        fetchPayerFromUrl();
    }, [id, type]);

    /** ---------------------------
     * Select guest handler
     * -------------------------- */

    const handleGuestSelect = (guest) => {
        setSelectedGuest(guest);

        // Push guest to URL
        const query = new URLSearchParams();
        query.set('id', payerType === 'corporate' ? guest.code : guest.op_number);
        query.set('type', payerType);

        Location.navigate({ url: `?${query.toString()}` });
    };

    /** ---------------------------
     * Cancel → return to search
     * -------------------------- */

    const handleCancel = () => {
        setSelectedGuest(null);
        Location.navigate({ url: window.location.pathname }); // clears ?id&type
    };

    /** ---------------------------
     * Go to add advance page
     * -------------------------- */

    // Redirect to collection entry
    const goToCollectionAdd = () => {
        // Redirect to collection entry
        const redirectLink = `/collection-entry/${payerType === 'corporate' ? selectedGuest?.code : selectedGuest?.op_number}?mode=${payerType}`; // checking corporate or guest

        Location.navigate({
            url: `${redirectLink}`,
        });
    };

    return (
        <Card className="card">
            {!selectedGuest && <SectionHeaderonHeader title="Advance Collection" subtitle="Please search below to locate your profile" />}
            {/* If no guest, show payer + search; else show guest info */}
            {!selectedGuest ? (
                <Row style={{ gap: '20px' }}>
                    {/* Dropdown to choose payer type */}
                    <PayerSelector value={payerType} onChange={setPayerType} />

                    {/* Search for guest and update state on select */}
                    <PayerSearch
                        onSelect={handleGuestSelect} // now receives full guest object
                        payerType={payerType}
                    />

                    {payerType === 'guest' ? (
                        <div style={{ alignSelf: 'flex-start', marginTop: '20px' }}>
                            <OpregSearch
                                enableVisit={false}
                                callback={(opreg) => {
                                    handleGuestSelect(opreg);
                                }}
                                model={Opreg}
                                title={t('Advance Search')}
                                shouldMigrateAccount={false}
                            />
                        </div>
                    ) : null}
                </Row>
            ) : (
                // Show guest info once selected
                <GuestInfo guest={selectedGuest} onCancel={handleCancel} />
            )}

            {/* add advance */}
            {selectedGuest && (
                <div className="advance-actions">
                    <Button type="primary" onClick={goToCollectionAdd}>
                        {t('+ Add Advance')}
                    </Button>
                </div>
            )}
            <br></br>
            {/* Show previous advance history when guest is selected */}
            <div>
                {selectedGuest && (
                    <ReportingDashboard
                        reportId={props.reportId}
                        scope={{
                            refno: effectiveType === payerAvailableTypes.GUEST ? selectedGuest?.op_number : selectedGuest?.code,
                            mode: payerModes[effectiveType],
                        }}
                        dbPtr={props.dbPtr}
                    ></ReportingDashboard>
                )}
            </div>
        </Card>
    );
};

export default AdvanceCollection;
